import { ConfigProvider, theme as antdTheme } from 'antd';
import type { MappingAlgorithm, ThemeConfig } from 'antd';
import { palette } from '../theme/palette';

/**
 * darkAlgorithm treats colorPrimary/colorSuccess/colorError as *seeds* and
 * regenerates a ramp from them, so #E8B14C would ship as #c89944. Seed keys are
 * also stripped from the `token` override before it is merged
 * (antd/es/theme/util/alias.js), meaning they cannot be pinned there either.
 *
 * Chaining a second derivative works because the theme reduces the algorithm
 * array (@ant-design/cssinjs/es/theme/Theme.js) — this one runs last and wins.
 */
const pinPalette: MappingAlgorithm = (_seed, mapToken) => ({
    ...mapToken!,

    // surfaces
    colorBgBase: palette.bg,
    colorBgLayout: palette.bg,
    colorBgContainer: palette.surface,
    colorBgElevated: palette.surfaceRaised,
    colorBgSpotlight: palette.surfaceRaised,
    colorBgMask: palette.mask,

    // borders
    colorBorder: palette.border,
    colorBorderSecondary: palette.border,

    // text
    colorTextBase: palette.text,
    colorText: palette.text,
    colorTextSecondary: palette.textSecondary,
    colorTextTertiary: palette.muted,
    colorTextQuaternary: palette.faint,

    // accent
    colorPrimaryBg: palette.accentBg,
    colorPrimaryBgHover: palette.accentBg,
    colorPrimaryBorder: palette.accentBorder,
    colorPrimaryBorderHover: palette.accentHover,
    colorPrimaryHover: palette.accentHover,
    colorPrimary: palette.accent,
    colorPrimaryActive: palette.accentPressed,
    colorPrimaryText: palette.accent,
    colorPrimaryTextHover: palette.accentHover,
    colorPrimaryTextActive: palette.accentPressed,
    colorLink: palette.accent,
    colorLinkHover: palette.accentHover,
    colorLinkActive: palette.accentPressed,

    // success
    colorSuccessBg: palette.successBg,
    colorSuccessBgHover: palette.successBg,
    colorSuccessBorder: palette.successBorder,
    colorSuccessBorderHover: palette.success,
    colorSuccessHover: palette.success,
    colorSuccess: palette.success,
    colorSuccessActive: palette.success,
    colorSuccessText: palette.success,
    colorSuccessTextHover: palette.success,
    colorSuccessTextActive: palette.success,

    // error
    colorErrorBg: palette.dangerBg,
    colorErrorBgHover: palette.dangerBg,
    colorErrorBorder: palette.dangerBorder,
    colorErrorBorderHover: palette.danger,
    colorErrorHover: palette.danger,
    colorError: palette.danger,
    colorErrorActive: palette.danger,
    colorErrorText: palette.danger,
    colorErrorTextHover: palette.danger,
    colorErrorTextActive: palette.danger,
});

export const darkTheme: ThemeConfig = {
    algorithm: [antdTheme.darkAlgorithm, pinPalette],

    // Seeds only. The exact values are pinned by pinPalette above; these are
    // here so the ramps we don't pin are generated in the right neighbourhood.
    token: {
        colorPrimary: palette.accent,
        colorSuccess: palette.success,
        colorError: palette.danger,
        colorInfo: palette.accent,
        colorBgBase: palette.bg,
        colorTextBase: palette.text,

        borderRadius: 12,
        controlHeight: 48, // gives every antd control a 48px tap target
        fontSize: 15,
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    },

    components: {
        // Primary label defaults to colorTextLightSolid (#fff) — 1.9:1 on gold.
        Button: {
            primaryColor: palette.onAccent,
            defaultBg: palette.surfaceRaised,
            defaultColor: palette.text,
            defaultBorderColor: palette.border,
            defaultHoverBg: palette.surfaceRaised,
            defaultHoverColor: palette.accent,
            defaultHoverBorderColor: palette.accent,
            defaultActiveBg: palette.surface,
            defaultActiveColor: palette.accentPressed,
            defaultActiveBorderColor: palette.accentPressed,
            primaryShadow: 'none',
            defaultShadow: 'none',
            dangerShadow: 'none',
            fontWeight: 600,
        },

        // Checkmark is colorWhite — same white-on-gold problem as Button.
        Checkbox: {
            colorWhite: palette.onAccent,
            controlInteractiveSize: 22,
        },

        // ant-list-empty-text renders at colorTextDisabled (0.25 alpha).
        List: {
            colorTextDisabled: palette.muted,
            itemPadding: '14px 0',
        },

        // Default 384px overflows a 360px viewport.
        Notification: {
            width: 'calc(100vw - 24px)',
        },

        Result: {
            paddingLG: 16,
            iconFontSize: 56,
            titleFontSize: 20,
            subtitleFontSize: 14,
        },

        Card: {
            paddingLG: 16,
        },

        Tag: {
            defaultBg: palette.surfaceRaised,
            defaultColor: palette.text,
        },

        Modal: {
            titleColor: palette.text,
        },

        Divider: {
            colorSplit: palette.border,
        },
    },
};

/**
 * Modal.confirm/info/warning are static: they read a module-global theme via
 * globalConfig().getTheme() (antd/es/modal/confirm.js) and never see the
 * <ConfigProvider> below. Without this call those dialogs render light.
 */
ConfigProvider.config({ theme: darkTheme });

const DefaultTheme = ({ children }: { children: any }) => {
    return <ConfigProvider theme={darkTheme}>{children}</ConfigProvider>;
};

export default DefaultTheme;
